import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, LogOut, Plus, ExternalLink, Building2, Check, UserPlus, Users, ShieldCheck, ChevronDown, Wallet, ReceiptText, CalendarDays, ImageOff, Megaphone, MailCheck, BookUser, Inbox, Contact, CircleHelp, Store, Blocks, Gift } from "lucide-react";
import { Logo } from "./Logo";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LanguageToggle } from "./LanguageToggle";
import { api, errorMessage, organizerGuideUrl } from "../lib/api";
import { hasUnseenChangelog } from "../lib/changelog";

export const DashboardLayout = ({ children }) => {
  const { organizer, logout, switchOrganization } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [myOrgs, setMyOrgs] = useState([]);
  const [imageSummary, setImageSummary] = useState(null);
  const [unreadReplies, setUnreadReplies] = useState(0);
  const [trackingHealth, setTrackingHealth] = useState(null); // ok | error | off – prikk ved «Svar (admin)»
  const [confirmImageCleanup, setConfirmImageCleanup] = useState(false);
  const [cleaningImages, setCleaningImages] = useState(false);
  const [unseenNews, setUnseenNews] = useState(hasUnseenChangelog);

  useEffect(() => {
    api
      .get("/auth/my-organizations")
      .then((res) => setMyOrgs(res.data))
      .catch(() => {});
  }, [organizer?.id]);

  useEffect(() => {
    if (!organizer?.is_admin) return;
    api
      .get("/admin/images/summary")
      .then((res) => setImageSummary(res.data))
      .catch(() => {});
  }, [organizer?.id, organizer?.is_admin]);

  // Uleste svar fra kontaktregisteret (admin) – teller i menyen, oppdateres ved sidebytte
  useEffect(() => {
    if (!organizer?.is_admin) return;
    api
      .get("/admin/prospects/replies/unread-count")
      .then((res) => setUnreadReplies(res.data?.unread || 0))
      .catch(() => {});
  }, [organizer?.id, organizer?.is_admin, location.pathname]);

  // Helse for svar-sporing (MX, Brevo-webhook, nøkler) – mellomlagret 10 min på serveren, så dette er billig
  useEffect(() => {
    if (!organizer?.is_admin) return;
    api
      .get("/admin/prospects/replies/health")
      .then((res) => setTrackingHealth(res.data))
      .catch(() => {});
  }, [organizer?.id, organizer?.is_admin, location.pathname]);

  const handleImageCleanup = async () => {
    setConfirmImageCleanup(false);
    setCleaningImages(true);
    try {
      const res = await api.post("/admin/images/cleanup");
      toast.success(t("{n} ubrukte bilder slettet", { n: res.data.deleted }));
      setImageSummary((prev) => (prev ? { ...prev, total: prev.total - res.data.deleted, unused: 0 } : prev));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke rydde bilder")));
    } finally {
      setCleaningImages(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleSwitch = async (org) => {
    if (org.current) return;
    try {
      await switchOrganization(org.id);
      toast.success(t("Byttet til {name}", { name: org.org_name }));
      window.location.assign("/dashboard");
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke bytte organisasjon")));
    }
  };

  const otherOrgs = myOrgs.filter((o) => !o.current);

  const initials = (organizer?.org_name || "A")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <Link to="/dashboard" className="flex items-center shrink-0" data-testid="dash-logo-link">
              <span className="hidden sm:block"><Logo /></span>
              <span className="sm:hidden"><Logo markClassName="w-9 h-9" textClassName="hidden" /></span>
            </Link>
            {/* Under xl vises bare ikoner (tekst fra xl og opp) så toppmenyen aldri blir bredere enn plassen – også på nynorsk.
                Budsjett (maks 1088 px innhold ved xl): fem lenker med tekst til venstre + språk, nytt, opprett, logg ut og avatar til høyre. */}
            <nav className="flex items-center gap-0.5" aria-label={t("Dashbord")}>
              <Button
                asChild
                variant={location.pathname === "/dashboard" ? "secondary" : "ghost"}
                size="sm"
                className="px-2.5"
                data-testid="dash-nav-overview"
              >
                <Link to="/dashboard" aria-label={t("Oversikt")} title={t("Oversikt")}>
                  <LayoutDashboard className="w-4 h-4 xl:mr-1.5" />
                  <span className="hidden xl:inline">{t("Oversikt")}</span>
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="px-2.5" data-testid="dash-nav-public">
                <Link to="/" aria-label={t("Se offentlig side")} title={t("Se offentlig side")}>
                  <ExternalLink className="w-4 h-4 xl:mr-1.5" />
                  <span className="hidden xl:inline">{t("Offentlig side")}</span>
                </Link>
              </Button>
              <Button
                asChild
                variant={location.pathname === "/dashboard/team" ? "secondary" : "ghost"}
                size="sm"
                className="px-2.5"
                data-testid="dash-nav-team"
              >
                <Link to="/dashboard/team" aria-label={t("Team")} title={t("Team")}>
                  <Users className="w-4 h-4 xl:mr-1.5" />
                  <span className="hidden xl:inline">{t("Team")}</span>
                </Link>
              </Button>
              <Button
                asChild
                variant={location.pathname === "/dashboard/betaling" ? "secondary" : "ghost"}
                size="sm"
                className="px-2.5"
                data-testid="dash-nav-payment"
              >
                <Link to="/dashboard/betaling" aria-label={t("Betaling")} title={t("Betaling")}>
                  <Wallet className="w-4 h-4 xl:mr-1.5" />
                  <span className="hidden xl:inline">{t("Betaling")}</span>
                </Link>
              </Button>
              {/* Tilleggsmodular (Butikk, Basar) – éin inngang i toppmenyen (plassbudsjettet tillèt ikkje ein knapp per modul), aktiv på /dashboard/butikk* og /dashboard/basar*.
                  Berre ikon + pil (teksten ligg i menyen) så toppmenyen held seg innanfor 1088 px òg på nynorsk. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={location.pathname.startsWith("/dashboard/butikk") || location.pathname.startsWith("/dashboard/basar") ? "secondary" : "ghost"}
                    size="sm"
                    className="px-2"
                    aria-label={t("Moduler: Butikk og Basar")}
                    title={t("Moduler: Butikk og Basar")}
                    data-testid="dash-nav-modules"
                  >
                    <Blocks className="w-4 h-4" />
                    <ChevronDown className="w-3.5 h-3.5 ml-0.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wide">{t("Tilleggsmoduler")}</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/butikk")} data-testid="dash-nav-shop">
                    <Store className="w-4 h-4 mr-2 shrink-0" />
                    <span className="min-w-0">
                      <span className="block">{t("Butikk")}</span>
                      <span className="block text-xs text-muted-foreground">{t("Selg varer med Vipps eller faktura")}</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/basar")} data-testid="dash-nav-basar">
                    <Gift className="w-4 h-4 mr-2 shrink-0" />
                    <span className="min-w-0">
                      <span className="block">{t("Basar")}</span>
                      <span className="block text-xs text-muted-foreground">{t("Loddsalg med Vipps og digital trekning")}</span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Rettleiinga «Så enkelt er det å komme i gang» (PDF) på valt språk – same dokument som F1 opnar.
                  På telefon (< sm) er det ikkje plass til eit femte ikon ved sida av logo, språk og avatar; der ligg «Hjelp» i brukarmenyen. */}
              <Button asChild variant="ghost" size="sm" className="px-2.5 hidden sm:inline-flex" data-testid="dash-nav-help">
                <a
                  href={organizerGuideUrl(lang)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("Hjelp (F1) – veiledning for arrangører (PDF)")}
                  title={t("Hjelp (F1) – veiledning for arrangører (PDF)")}
                >
                  <CircleHelp className="w-4 h-4 xl:mr-1.5" />
                  <span className="hidden xl:inline">{t("Hjelp (F1)")}</span>
                </a>
              </Button>
            </nav>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <LanguageToggle compact />
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="relative hidden sm:flex"
              data-testid="dash-whats-new-button"
            >
              <Link to="/nyheter" onClick={() => setUnseenNews(false)} aria-label={t("Hva er nytt")} title={t("Hva er nytt")}>
                <Megaphone className="w-4 h-4" />
                <span className="sr-only">{t("Hva er nytt")}</span>
                {unseenNews && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5" data-testid="dash-whats-new-dot">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--accent))] opacity-60"></span>
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--accent))]"></span>
                  </span>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" className="hidden md:flex" data-testid="dash-create-event-button">
              <Link to="/dashboard/ny">
                <Plus className="w-4 h-4 mr-1.5" />
                {t("Opprett arrangement")}
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground hidden md:inline-flex"
              data-testid="dash-logout-header-button"
              aria-label={t("Logg ut")}
              title={t("Logg ut")}
            >
              <LogOut className="w-4 h-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-0.5 rounded-full focus:outline-none focus:ring-2 focus:ring-ring" data-testid="dash-user-menu-trigger">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-accent text-accent-foreground font-medium text-sm">{initials}</AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <p className="font-medium">{organizer?.org_name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{organizer?.email}</p>
                </DropdownMenuLabel>
                {otherOrgs.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal uppercase tracking-wide">
                      {t("Bytt organisasjon")}
                    </DropdownMenuLabel>
                    {otherOrgs.map((o) => (
                      <DropdownMenuItem key={o.id} onClick={() => handleSwitch(o)} data-testid={`org-switch-${o.id}`}>
                        <Building2 className="w-4 h-4 mr-2 shrink-0" />
                        <span className="min-w-0">
                          <span className="block truncate">{o.org_name}</span>
                          <span className="block text-xs text-muted-foreground">{t("Org.nr {n}", { n: o.org_number || "–" })}</span>
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                {organizer?.is_admin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/godkjenninger")} data-testid="admin-approvals-button">
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      {t("Godkjenninger")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/fakturaer-admin")} data-testid="admin-invoices-button">
                      <ReceiptText className="w-4 h-4 mr-2" />
                      {t("Fakturaer (admin)")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/arrangementer-admin")} data-testid="admin-events-button">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      {t("Arrangementer (admin)")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/epost-admin")} data-testid="admin-email-button">
                      <MailCheck className="w-4 h-4 mr-2" />
                      {t("E-postbekreftelse (admin)")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/kontaktregister-admin")} data-testid="admin-prospects-button">
                      <BookUser className="w-4 h-4 mr-2" />
                      {t("Kontaktregister (admin)")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/vipps-admin")} data-testid="admin-vipps-button">
                      <Wallet className="w-4 h-4 mr-2" />
                      {t("Vipps (admin)")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard/svar")} data-testid="admin-replies-button">
                      <Inbox className="w-4 h-4 mr-2" />
                      <span className="flex-1">{t("Svar (admin)")}</span>
                      {trackingHealth && trackingHealth.status !== "off" && (
                        <span
                          className={`ml-2 inline-block w-2.5 h-2.5 rounded-full shrink-0 ${trackingHealth.status === "ok" ? "bg-[hsl(var(--success))]" : "bg-[hsl(var(--danger))]"}`}
                          role="img"
                          aria-label={trackingHealth.status === "ok" ? t("Svar-sporing OK") : t("Svar-sporing har problemer: {list}", { list: (trackingHealth.problems || []).join(", ") })}
                          title={trackingHealth.status === "ok" ? t("Svar-sporing OK") : t("Svar-sporing har problemer: {list}", { list: (trackingHealth.problems || []).join(", ") })}
                          data-testid="admin-reply-tracking-health-dot"
                          data-status={trackingHealth.status}
                        />
                      )}
                      {unreadReplies > 0 && (
                        <Badge className="ml-2 h-5 px-1.5 text-[11px] bg-brand text-brand-foreground hover:bg-brand" data-testid="admin-replies-unread-count">{unreadReplies}</Badge>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setConfirmImageCleanup(true)}
                      disabled={cleaningImages || !imageSummary || imageSummary.unused === 0}
                      data-testid="admin-image-cleanup-button"
                    >
                      <ImageOff className="w-4 h-4 mr-2" />
                      <span className="flex-1">{t("Bilderydding (admin)")}</span>
                      <Badge
                        variant={imageSummary?.unused ? "default" : "secondary"}
                        className="ml-2"
                        data-testid="admin-unused-images-badge"
                      >
                        {imageSummary ? imageSummary.unused : "…"}
                      </Badge>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate("/dashboard/kontaktinfo")} data-testid="org-contact-button">
                  <Contact className="w-4 h-4 mr-2" />
                  <span className="flex-1">{t("Kontaktinfo (offentlig)")}</span>
                  {organizer?.public_contact_complete === false && (
                    <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[hsl(var(--warning))]" aria-label={t("Kontaktinfo mangler")} title={t("Kontaktinfo mangler")} data-testid="org-contact-missing-dot" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/registrer")} data-testid="org-add-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t("Legg til organisasjon")}
                </DropdownMenuItem>
                {/* Telefon: «Hjelp» her i staden for i toppmenyen (sjå kommentar ved dash-nav-help) */}
                <DropdownMenuItem asChild className="sm:hidden" data-testid="dash-menu-help">
                  <a href={organizerGuideUrl(lang)} target="_blank" rel="noopener noreferrer">
                    <CircleHelp className="w-4 h-4 mr-2" />
                    {t("Hjelp – veiledning (PDF)")}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="dash-logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("Logg ut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <AlertDialog open={confirmImageCleanup} onOpenChange={setConfirmImageCleanup}>
        <AlertDialogContent data-testid="image-cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Slette {n} ubrukte bilder?", { n: imageSummary?.unused ?? 0 })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("Dette fjerner permanent alle bilder som ikke er i bruk av noe arrangement. Bilder som er i bruk, påvirkes ikke. Handlingen kan ikke angres.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="image-cleanup-cancel">{t("Avbryt")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleImageCleanup} data-testid="image-cleanup-confirm">
              {t("Slett ubrukte bilder")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
};
